import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import {
    FileText, Send, RefreshCw, Trash2, CheckCircle, AlertCircle,
    Clock, Search, X, Plus, ExternalLink, Mail, FlaskConical, Loader2,
    Edit2, Eye, ClipboardCheck
} from "lucide-react";

const PLANS = [
    { value: "starter",      label: "TinyGlobal Starter",       price: "$99–149/mo" },
    { value: "growth",       label: "TinyGlobal Growth",        price: "$249–399/mo" },
    { value: "professional", label: "TinyGlobal Professional",  price: "$599+/mo" },
];

const STATUS_CFG = {
    draft:  { cls: "bg-slate-100 text-slate-600",   label: "Draft" },
    sent:   { cls: "bg-blue-100 text-blue-700",     label: "Sent — Awaiting Signature" },
    signed: { cls: "bg-emerald-100 text-emerald-700", label: "Signed" },
};

function StatusBadge({ status }) {
    const cfg = STATUS_CFG[status] || { cls: "bg-slate-100 text-slate-500", label: status };
    return <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

export default function SLAManager() {
    const [slas, setSlas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Create modal
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({
        tenantId: "", contactName: "", contactEmail: "",
        plan: "starter", startDate: "", term: "monthly", customTerms: "",
    });
    const [creating, setCreating] = useState(false);

    // Preview modal
    const [previewing, setPreviewing] = useState(null);

    // Test email
    const [showTest, setShowTest] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    // Test agreement (full end-to-end)
    const [showTestAgreement, setShowTestAgreement] = useState(false);
    const [testAgreementTo, setTestAgreementTo] = useState("");
    const [testingAgreement, setTestingAgreement] = useState(false);
    const [testAgreementResult, setTestAgreementResult] = useState(null);

    // Edit SLA
    const [editingSLA, setEditingSLA] = useState(null); // full sla object
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    // Preview SLA text
    const [previewSLA, setPreviewSLA] = useState(null);

    const flash = (msg, type = "success") => {
        if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(""), 5000); }
        else { setError(msg); setTimeout(() => setError(""), 6000); }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/api/sla");
            setSlas(res.data.slas || []);
        } catch (e) {
            flash(e.response?.data?.error || e.message, "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        getDocs(query(collection(db, "companies"), orderBy("name")))
            .then(snap => {
                const list = [];
                snap.forEach(d => {
                    const data = d.data();
                    if (data.status !== "deleted") {
                        list.push({ id: d.id, name: data.name || d.id, contactEmail: data.contactEmail || "", paymentPlan: data.paymentPlan || "starter" });
                    }
                });
                setCompanies(list);
            }).catch(() => {});
    }, []);

    const handleTenantSelect = (tenantId) => {
        const company = companies.find(c => c.id === tenantId);
        setForm(f => ({
            ...f,
            tenantId,
            contactEmail: company?.contactEmail || f.contactEmail,
            plan: company?.paymentPlan || f.plan,
        }));
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.tenantId || !form.contactName || !form.contactEmail || !form.plan) {
            return flash("All required fields must be filled.", "error");
        }
        setCreating(true);
        try {
            const res = await api.post("/api/sla", form);
            flash(`SLA sent to ${form.contactEmail} for signature.`);
            setShowCreate(false);
            setForm({ tenantId: "", contactName: "", contactEmail: "", plan: "starter", startDate: "", term: "monthly", customTerms: "" });
            load();
        } catch (e) {
            flash(e.response?.data?.error || e.message, "error");
        } finally {
            setCreating(false);
        }
    };

    const handleResend = async (id, email) => {
        try {
            await api.post(`/api/sla/${id}/resend`);
            flash(`Signing email resent to ${email}.`);
        } catch (e) {
            flash(e.response?.data?.error || e.message, "error");
        }
    };

    const handleTestAgreement = async () => {
        if (!testAgreementTo) return;
        setTestingAgreement(true);
        setTestAgreementResult(null);
        try {
            const res = await api.post("/api/sla/test-agreement", { to: testAgreementTo });
            setTestAgreementResult({ ok: true, message: `Test agreement sent to ${testAgreementTo}. Opening signing link…`, signingUrl: res.data.signingUrl });
            window.open(res.data.signingUrl, "_blank");
            load();
        } catch (e) {
            const status = e.response?.status;
            const msg = status === 404
                ? "Backend not reachable — make sure the server is running on port 4000."
                : e.response?.data?.error || e.message;
            setTestAgreementResult({ ok: false, message: msg });
        } finally {
            setTestingAgreement(false);
        }
    };

    const openEdit = (sla) => {
        setEditingSLA(sla);
        setEditForm({
            contactName: sla.contactName || "",
            contactEmail: sla.contactEmail || "",
            plan: sla.plan || "starter",
            term: sla.term || "monthly",
            startDate: sla.startDate || "",
            customTerms: sla.customTerms || "",
        });
    };

    const handleSaveEdit = async () => {
        setSaving(true);
        try {
            await api.patch(`/api/sla/${editingSLA.id}`, editForm);
            flash("SLA updated successfully.");
            setEditingSLA(null);
            load();
        } catch (e) {
            flash(e.response?.data?.error || e.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleTestEmail = async () => {
        if (!testTo) return;
        setTesting(true);
        setTestResult(null);
        try {
            const res = await api.post("/api/sla/test-email", { to: testTo });
            setTestResult({ ok: true, message: `Test email delivered to ${testTo} (id: ${res.data.emailId})` });
        } catch (e) {
            setTestResult({ ok: false, message: e.response?.data?.error || e.message });
        } finally {
            setTesting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this SLA? This cannot be undone.")) return;
        try {
            await api.delete(`/api/sla/${id}`);
            flash("SLA deleted.");
            load();
        } catch (e) {
            flash(e.response?.data?.error || e.message, "error");
        }
    };

    const formatDate = (iso) => {
        if (!iso) return "—";
        return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
    };

    const filtered = slas.filter(s =>
        s.tenantName?.toLowerCase().includes(search.toLowerCase()) ||
        s.contactEmail?.toLowerCase().includes(search.toLowerCase()) ||
        s.planLabel?.toLowerCase().includes(search.toLowerCase())
    );

    const stats = {
        total: slas.length,
        sent: slas.filter(s => s.status === "sent").length,
        signed: slas.filter(s => s.status === "signed").length,
    };

    return (
        <div className="max-w-7xl mx-auto py-8 px-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif flex items-center gap-3">
                        <FileText size={28} className="text-gold-500"/> Service Level Agreements
                    </h1>
                    <p className="text-slate-500 mt-1">Create, send, and track signed SLAs with your clients.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={() => { setShowTest(true); setTestResult(null); setTestTo(""); }}
                        className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold flex items-center gap-2 hover:bg-slate-50 transition-colors shadow-sm text-sm"
                    >
                        <FlaskConical size={15}/> Test Email
                    </button>
                    <button
                        onClick={() => { setShowTestAgreement(true); setTestAgreementResult(null); setTestAgreementTo(""); }}
                        className="px-4 py-2.5 bg-white border border-violet-200 text-violet-700 rounded-xl font-bold flex items-center gap-2 hover:bg-violet-50 transition-colors shadow-sm text-sm"
                    >
                        <ClipboardCheck size={15}/> Test Agreement
                    </button>
                    <button
                        onClick={() => setShowCreate(true)}
                        className="px-5 py-2.5 bg-navy-900 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-navy-800 transition-colors shadow-lg shadow-navy-900/20 text-sm"
                    >
                        <Plus size={18}/> Create SLA
                    </button>
                </div>
            </div>

            {/* Alerts */}
            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}
            {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2"><CheckCircle size={16}/>{success}</div>}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                    { label: "Total SLAs", value: stats.total, cls: "" },
                    { label: "Awaiting Signature", value: stats.sent, cls: stats.sent > 0 ? "border-blue-200 bg-blue-50" : "" },
                    { label: "Signed & Active", value: stats.signed, cls: "" },
                ].map((m, i) => (
                    <div key={i} className={`bg-white rounded-2xl border border-slate-200 p-5 shadow-sm ${m.cls}`}>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{m.label}</p>
                        <p className="text-2xl font-bold text-navy-900">{m.value}</p>
                    </div>
                ))}
            </div>

            {/* Search */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex items-center gap-3">
                <Search size={16} className="text-slate-400"/>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by company, email, or plan..."
                    className="flex-1 text-sm focus:outline-none text-slate-700 placeholder-slate-400"/>
                {search && <button onClick={() => setSearch("")}><X size={15} className="text-slate-400"/></button>}
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left">
                    <thead className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/30">
                        <tr>
                            <th className="px-6 py-4">Company</th>
                            <th className="px-6 py-4">Plan</th>
                            <th className="px-6 py-4">Contact</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Sent</th>
                            <th className="px-6 py-4">Signed</th>
                            <th className="px-6 py-4"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="7" className="px-6 py-8 text-center text-slate-500">Loading...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan="7" className="px-6 py-8 text-center text-slate-500">No SLAs found. Create one to get started.</td></tr>
                        ) : filtered.map(sla => (
                            <tr key={sla.id} className="hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-4">
                                    <p className="font-bold text-navy-900">{sla.tenantName}</p>
                                    <p className="text-[11px] text-slate-400">{sla.term === "annual" ? "Annual" : "Monthly"}</p>
                                </td>
                                <td className="px-6 py-4">
                                    <span className="px-2 py-0.5 bg-navy-50 border border-navy-100 text-navy-700 text-[10px] font-bold rounded-full">
                                        {sla.planLabel || sla.plan}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <p className="text-sm text-slate-700">{sla.contactName}</p>
                                    <p className="text-[11px] text-slate-400">{sla.contactEmail}</p>
                                </td>
                                <td className="px-6 py-4"><StatusBadge status={sla.status}/></td>
                                <td className="px-6 py-4 text-sm text-slate-500">{formatDate(sla.sentAt)}</td>
                                <td className="px-6 py-4">
                                    {sla.status === "signed" ? (
                                        <div>
                                            <p className="text-sm text-emerald-600 font-semibold">{formatDate(sla.signedAt)}</p>
                                            {sla.signerName && <p className="text-[11px] text-slate-400">by {sla.signerName}</p>}
                                        </div>
                                    ) : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-1">
                                        <button onClick={() => setPreviewSLA(sla)}
                                            title="Preview agreement"
                                            className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 transition-all">
                                            <Eye size={15}/>
                                        </button>
                                        {sla.status !== "signed" && (
                                            <button onClick={() => openEdit(sla)}
                                                title="Edit SLA"
                                                className="p-2 hover:bg-amber-50 hover:text-amber-600 rounded-lg text-slate-400 transition-all">
                                                <Edit2 size={15}/>
                                            </button>
                                        )}
                                        {sla.status !== "signed" && (
                                            <button onClick={() => handleResend(sla.id, sla.contactEmail)}
                                                title="Resend signing email"
                                                className="p-2 hover:bg-blue-600 hover:text-white rounded-lg text-slate-400 transition-all">
                                                <Mail size={15}/>
                                            </button>
                                        )}
                                        {sla.status !== "signed" && (
                                            <button onClick={() => handleDelete(sla.id)}
                                                title="Delete"
                                                className="p-2 hover:bg-red-500 hover:text-white rounded-lg text-slate-400 transition-all">
                                                <Trash2 size={15}/>
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Test Email Modal */}
            {showTest && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between rounded-t-2xl">
                            <h2 className="text-base font-bold text-navy-900 flex items-center gap-2">
                                <FlaskConical size={16}/> Test SLA Email Delivery
                            </h2>
                            <button onClick={() => setShowTest(false)} className="p-1.5 text-slate-400 hover:bg-slate-200 rounded-lg">
                                <X size={16}/>
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-500">Sends a real SLA signing email to verify Resend is delivering correctly.</p>
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Send test to</label>
                                <input
                                    type="email"
                                    value={testTo}
                                    onChange={e => setTestTo(e.target.value)}
                                    placeholder="your@email.com"
                                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                />
                            </div>
                            {testResult && (
                                <div className={`flex items-start gap-2 px-4 py-3 rounded-xl text-sm border ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                                    {testResult.ok ? <CheckCircle size={16} className="shrink-0 mt-0.5"/> : <AlertCircle size={16} className="shrink-0 mt-0.5"/>}
                                    {testResult.message}
                                </div>
                            )}
                            <div className="flex justify-end gap-3">
                                <button onClick={() => setShowTest(false)} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 text-sm">
                                    Close
                                </button>
                                <button onClick={handleTestEmail} disabled={testing || !testTo}
                                    className="px-5 py-2.5 rounded-xl bg-navy-900 text-white font-bold flex items-center gap-2 hover:bg-navy-800 disabled:opacity-40 transition-colors text-sm">
                                    {testing ? <Loader2 size={15} className="animate-spin"/> : <Send size={15}/>}
                                    {testing ? "Sending…" : "Send Test"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Test Agreement Modal */}
            {showTestAgreement && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="px-6 py-4 border-b border-slate-100 bg-violet-50 flex items-center justify-between rounded-t-2xl">
                            <h2 className="text-base font-bold text-navy-900 flex items-center gap-2">
                                <ClipboardCheck size={16} className="text-violet-600"/> Test Full Agreement Flow
                            </h2>
                            <button onClick={() => setShowTestAgreement(false)} className="p-1.5 text-slate-400 hover:bg-violet-100 rounded-lg">
                                <X size={16}/>
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-500">Creates a real test SLA, sends a signing email, and opens the signing link — full end-to-end verification.</p>
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Send test to</label>
                                <input
                                    type="email"
                                    value={testAgreementTo}
                                    onChange={e => setTestAgreementTo(e.target.value)}
                                    placeholder="client@email.com"
                                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400/30"
                                />
                            </div>
                            {testAgreementResult && (
                                <div className={`flex items-start gap-2 px-4 py-3 rounded-xl text-sm border ${testAgreementResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                                    {testAgreementResult.ok ? <CheckCircle size={16} className="shrink-0 mt-0.5"/> : <AlertCircle size={16} className="shrink-0 mt-0.5"/>}
                                    {testAgreementResult.message}
                                </div>
                            )}
                            <div className="flex justify-end gap-3">
                                <button onClick={() => setShowTestAgreement(false)} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 text-sm">
                                    Close
                                </button>
                                <button onClick={handleTestAgreement} disabled={testingAgreement || !testAgreementTo}
                                    className="px-5 py-2.5 rounded-xl bg-violet-600 text-white font-bold flex items-center gap-2 hover:bg-violet-700 disabled:opacity-40 transition-colors text-sm">
                                    {testingAgreement ? <Loader2 size={15} className="animate-spin"/> : <ClipboardCheck size={15}/>}
                                    {testingAgreement ? "Sending…" : "Send Test Agreement"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit SLA Modal */}
            {editingSLA && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl flex flex-col max-h-[92vh] overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 bg-amber-50 flex items-center justify-between rounded-t-2xl shrink-0">
                            <div>
                                <h2 className="text-lg font-bold text-navy-900 flex items-center gap-2">
                                    <Edit2 size={18} className="text-amber-500"/> Edit SLA
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">{editingSLA.tenantName} — unsigned agreement</p>
                            </div>
                            <button onClick={() => setEditingSLA(null)} className="p-2 text-slate-400 hover:bg-amber-100 rounded-lg transition-colors">
                                <X size={18}/>
                            </button>
                        </div>

                        <div className="p-6 space-y-4 overflow-y-auto flex-1">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Contact Name</label>
                                    <input type="text" value={editForm.contactName}
                                        onChange={e => setEditForm(f => ({ ...f, contactName: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-amber-400/30"/>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Contact Email</label>
                                    <input type="email" value={editForm.contactEmail}
                                        onChange={e => setEditForm(f => ({ ...f, contactEmail: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-amber-400/30"/>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Plan</label>
                                    <select value={editForm.plan}
                                        onChange={e => setEditForm(f => ({ ...f, plan: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-amber-400/30 bg-white">
                                        {PLANS.map(p => <option key={p.value} value={p.value}>{p.label} — {p.price}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Term</label>
                                    <select value={editForm.term}
                                        onChange={e => setEditForm(f => ({ ...f, term: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-amber-400/30 bg-white">
                                        <option value="monthly">Month-to-month</option>
                                        <option value="annual">Annual (12 months)</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Service Start Date</label>
                                <input type="date" value={editForm.startDate}
                                    onChange={e => setEditForm(f => ({ ...f, startDate: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-amber-400/30"/>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Additional Terms</label>
                                <textarea rows={4} value={editForm.customTerms}
                                    onChange={e => setEditForm(f => ({ ...f, customTerms: e.target.value }))}
                                    placeholder="Special arrangements, custom pricing, negotiated terms..."
                                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-amber-400/30 resize-none"/>
                            </div>

                            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-800">
                                Saving will regenerate the SLA document. The client will need to be resent the signing link to view the updated agreement.
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button onClick={() => setEditingSLA(null)}
                                    className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 transition-colors text-sm">
                                    Cancel
                                </button>
                                <button onClick={handleSaveEdit} disabled={saving}
                                    className="px-6 py-2.5 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 transition-colors flex items-center gap-2 shadow-lg shadow-amber-500/20 disabled:opacity-50 text-sm">
                                    {saving ? <Loader2 size={15} className="animate-spin"/> : <Edit2 size={15}/>}
                                    {saving ? "Saving…" : "Save Changes"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview SLA Text Modal */}
            {previewSLA && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl flex flex-col max-h-[92vh] overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between rounded-t-2xl shrink-0">
                            <div>
                                <h2 className="text-lg font-bold text-navy-900 flex items-center gap-2">
                                    <Eye size={18} className="text-slate-500"/> Agreement Preview
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {previewSLA.tenantName} · {previewSLA.planLabel || previewSLA.plan} · <StatusBadge status={previewSLA.status}/>
                                </p>
                            </div>
                            <button onClick={() => setPreviewSLA(null)} className="p-2 text-slate-400 hover:bg-slate-200 rounded-lg transition-colors">
                                <X size={18}/>
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            {previewSLA.slaText ? (
                                <pre className="whitespace-pre-wrap font-mono text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded-xl p-5 leading-relaxed">
                                    {previewSLA.slaText}
                                </pre>
                            ) : (
                                <div className="text-center py-12 text-slate-400">
                                    <FileText size={40} className="mx-auto mb-3 opacity-30"/>
                                    <p className="text-sm">No agreement text available for this SLA.</p>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-slate-100 flex justify-end shrink-0">
                            <button onClick={() => setPreviewSLA(null)}
                                className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 transition-colors text-sm">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create SLA Modal */}
            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl flex flex-col max-h-[92vh] overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between rounded-t-2xl shrink-0">
                            <div>
                                <h2 className="text-lg font-bold text-navy-900 flex items-center gap-2">
                                    <FileText size={18} className="text-gold-500"/> Create & Send SLA
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">Generates the agreement and sends a signing link to the client.</p>
                            </div>
                            <button onClick={() => setShowCreate(false)} className="p-2 text-slate-400 hover:bg-slate-200 rounded-lg transition-colors">
                                <X size={18}/>
                            </button>
                        </div>

                        <form onSubmit={handleCreate} className="p-6 space-y-4 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Company / Tenant *</label>
                                <select
                                    value={form.tenantId}
                                    onChange={e => handleTenantSelect(e.target.value)}
                                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 bg-white"
                                    required
                                >
                                    <option value="">— Select tenant —</option>
                                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Contact Name *</label>
                                    <input type="text" value={form.contactName}
                                        onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))}
                                        placeholder="Jane Smith"
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                        required/>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Contact Email *</label>
                                    <input type="email" value={form.contactEmail}
                                        onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))}
                                        placeholder="jane@company.com"
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                        required/>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Plan *</label>
                                    <select value={form.plan}
                                        onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 bg-white"
                                        required>
                                        {PLANS.map(p => <option key={p.value} value={p.value}>{p.label} — {p.price}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Term</label>
                                    <select value={form.term}
                                        onChange={e => setForm(f => ({ ...f, term: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 bg-white">
                                        <option value="monthly">Month-to-month</option>
                                        <option value="annual">Annual (12 months)</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Service Start Date</label>
                                <input type="date" value={form.startDate}
                                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"/>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Additional Terms (optional)</label>
                                <textarea rows={3} value={form.customTerms}
                                    onChange={e => setForm(f => ({ ...f, customTerms: e.target.value }))}
                                    placeholder="Any special arrangements, custom pricing notes, or negotiated terms..."
                                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 resize-none"/>
                            </div>

                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700">
                                <strong>What happens next:</strong> The SLA document is generated and a signing link is emailed to the client. Once they sign, both parties receive a copy with a full audit trail.
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowCreate(false)}
                                    className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 transition-colors">
                                    Cancel
                                </button>
                                <button type="submit" disabled={creating}
                                    className="px-6 py-2.5 rounded-xl bg-navy-900 text-white font-bold hover:bg-navy-800 transition-colors flex items-center gap-2 shadow-lg shadow-navy-900/20 disabled:opacity-50">
                                    {creating ? "Sending…" : <><Send size={16}/> Generate & Send</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
